/**
 * De iconen die de applicatie gebruikt, inline als SVG.
 *
 * Eenvoudige lijniconen op streekbreedte 1,5 en 14–16 px, zoals de designspecificatie
 * vraagt. De kleur volgt `currentColor`, dus een icoon in de zijbalk, op een knop of
 * in een chip neemt vanzelf de juiste kleur aan. Geen extra pakket, geen
 * netwerkverzoek.
 *
 * Het merkicoon is hier weg: op de plekken waar het stond (zijbalk, loginscherm)
 * staat het witte logo van De Klerk Binnenbouw uit `assets/dkb-logo-wit.svg`.
 */
type Props = { maat?: number; className?: string };

const basis = (maat: number) => ({
  width: maat,
  height: maat,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

// ── Navigatie en icoonrail ──────────────────────────────────────────────────

export const IconRaster = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <path d="M3 9h18M3 15h18M9 3v18" />
  </svg>
);

export const IconWerkvloer = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <rect x="3" y="4" width="18" height="13" rx="1" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

export const IconGrafiek = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
);

export const IconAgenda = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <rect x="3" y="5" width="18" height="16" rx="1.5" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export const IconBlokken = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <rect x="3" y="3" width="8" height="8" rx="1.5" />
    <rect x="13" y="13" width="8" height="8" rx="1.5" />
    <path d="M11 7h4a2 2 0 0 1 2 2v4" />
  </svg>
);

export const IconKubus = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M12 2.5 21 7v10l-9 4.5L3 17V7z" />
    <path d="M3 7l9 4.5L21 7M12 11.5V21.5" />
  </svg>
);

export const IconTandwiel = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 8.9 19a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 5 8.9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9.5a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

// ── Inklappen en uitvouwen ──────────────────────────────────────────────────

export const IconChevronLinks = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const IconChevronRechts = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const IconChevronOnder = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const IconHamburger = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// ── Handelingen ─────────────────────────────────────────────────────────────

export const IconVink = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const IconCamera = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

export const IconWaarschuwing = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

export const IconPrinter = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M6 9V2h12v7" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

export const IconDownload = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

export const IconUpload = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  </svg>
);

export const IconHerhaal = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M3 2v6h6" />
    <path d="M3.5 14a9 9 0 1 0 2.1-9.4L3 8" />
  </svg>
);

export const IconPlus = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconSplitsen = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M3 12h5l4-7h9M12 19h9M8 12l4 7" />
  </svg>
);

export const IconZoek = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4.3-4.3" />
  </svg>
);

/** Twee schermen: de werkvloer over twee monitoren openen. */
export const IconSchermen = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <rect x="2" y="5" width="9" height="11" rx="1" />
    <rect x="13" y="5" width="9" height="11" rx="1" />
    <path d="M5 19h4M16 19h4" />
  </svg>
);

// ── Zijbalk: onderste groep en gebruiker ────────────────────────────────────

export const IconReddingsboei = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4" />
    <path d="M4.9 4.9l4.3 4.3M14.8 14.8l4.3 4.3M14.8 9.2l4.3-4.3M4.9 19.1l4.3-4.3" />
  </svg>
);

export const IconBel = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

export const IconUitloggen = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

/** Zijbalk in- of uitklappen: een paneel met een pijl naar links of naar rechts. */
export const IconPaneel = ({ maat = 16, className, open = true }: Props & { open?: boolean }) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d={open ? 'M16 15l-3-3 3-3' : 'M14 9l3 3-3 3'} />
  </svg>
);

export const IconKruis = ({ maat = 16, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/** Zes stippen: hier pak je iets vast om het te slepen. */
export const IconGreep = ({ maat = 14, className }: Props) => (
  <svg {...basis(maat)} className={className} aria-hidden>
    {[9, 15].flatMap((x) =>
      [6, 12, 18].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill="currentColor" stroke="none" />),
    )}
  </svg>
);
